<template>
  <div class="video-player-container">
    <button class="player-help-btn" @click="showHelp = true" title="使用指南">
        <i class="mdi mdi-help-circle-outline"></i>
    </button>

    <video 
      v-if="src" 
      ref="videoRef"
      :src="src" 
      controls 
      class="video-element"
      @timeupdate="onTimeUpdate"
      @loadedmetadata="onLoadedMetadata"
      @play="onPlay"
      @pause="onPause"
    ></video>
    <div v-else class="placeholder">
      <i class="mdi mdi-movie-off-outline"></i>
      <span>暂无视频源</span>
    </div>

    <!-- Help Modal -->
    <Teleport to="body">
      <div v-if="showHelp" class="help-overlay" @click.self="showHelp = false">
        <div class="help-modal">
          <div class="help-header">
            <h3>播放器使用指南</h3>
            <button class="close-btn" @click="showHelp = false">×</button>
          </div>
          <div class="help-content">
            <div class="help-step">
              <div class="step-icon"><i class="mdi mdi-play-circle-outline"></i></div>
              <div class="step-info">
                <h4>预览与播放</h4>
                <p>此处展示您当前选中的“素材”或“剪辑池片段”。</p>
                <p>支持 <strong>空格键 (Space)</strong> 快速播放/暂停。</p>
              </div>
            </div>
            <div class="help-step">
              <div class="step-icon"><i class="mdi mdi-link-variant"></i></div>
              <div class="step-info">
                <h4>画面联动</h4>
                <p>播放时，右下方的“文稿视图 (SourceViewer)”会自动滚动并高亮当前读到的文字。</p>
              </div>
            </div>
          </div>
          <div class="help-footer">
            <button class="primary-btn" @click="showHelp = false">知道了</button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { ref, watch, onMounted, onBeforeUnmount } from 'vue'

export default {
  name: 'VideoPlayer',
  props: {
    src: {
      type: String,
      default: ''
    },
    // Used to trigger seek externally
    seekRequest: {
      type: Object, // { time: number }
      default: null
    }
  },
  emits: ['timeupdate', 'loadedmetadata', 'play', 'pause'],
  setup(props, { emit }) {
    const videoRef = ref(null)
    const pendingSeek = ref(null)
    const isPlaying = ref(false)
    const showHelp = ref(false)
    let rafId = null

    const handleKeydown = (e) => {
      // Avoid triggering when typing in inputs
      const tagName = e.target.tagName
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tagName) || e.target.isContentEditable) {
        return
      }
      
      if (e.code === 'Space') {
        e.preventDefault() // Prevent page scrolling
        if (videoRef.value) {
          if (videoRef.value.paused) {
            videoRef.value.play().catch(err => console.error('Play failed:', err))
          } else {
            videoRef.value.pause()
          }
        }
      }
    }

    onMounted(() => {
      window.addEventListener('keydown', handleKeydown)
    })
    
    onBeforeUnmount(() => {
        window.removeEventListener('keydown', handleKeydown)
        if (rafId) cancelAnimationFrame(rafId)
    })

    const loop = () => {
        if (!isPlaying.value) return
        if (videoRef.value) {
            // Check pendingSeek inside loop as well to prevent updates during seek
            if (pendingSeek.value === null) {
                emit('timeupdate', videoRef.value.currentTime)
            }
        }
        rafId = requestAnimationFrame(loop)
    }

    const onTimeUpdate = (e) => {
      // Suppress time updates if we have a pending seek, 
      // to avoid triggering logic (like auto-skip) with transient timestamps (e.g. 0 on source load)
      if (pendingSeek.value !== null) {
        return
      }
      // If playing, RAF handles updates with higher frequency.
      // We suppress native timeupdate to avoid duplicate/interleaved events.
      if (!isPlaying.value) {
          emit('timeupdate', e.target.currentTime)
      }
    }

    const onLoadedMetadata = (e) => {
      emit('loadedmetadata', e)
      const video = videoRef.value || e.target
      if (video && pendingSeek.value && pendingSeek.value.time !== undefined) {
        // Access time properly if pendingSeek is an object now
        video.currentTime = pendingSeek.value.time
        
        if (pendingSeek.value.playAfterSeek) {
           video.play().catch(ev => console.error('Auto-play failed:', ev))
        }
        
        // We don't clear pendingSeek immediately to ensure it sticks if multiple events fire,
        // but strictly speaking we executed it.
        // Let's clear it to prevent unwanted seeks if user manually scrubs later and metadata reloads (unlikely).
        pendingSeek.value = null
      }
    }

    const onPlay = () => {
        isPlaying.value = true
        emit('play')
        loop()
    }
    
    const onPause = () => {
        isPlaying.value = false
        if (rafId) cancelAnimationFrame(rafId)
        emit('pause')
    }

    // Public methods for parent component
    const playVideo = () => {
        if (videoRef.value) {
            videoRef.value.play().catch(e => console.error('Play failed:', e))
        }
    }

    const pauseVideo = () => {
        // console.log('[VideoPlayer] pauseVideo called')
        if (videoRef.value) {
            videoRef.value.pause()
        }
    }

    // Watch for source and seek changes
    watch([() => props.src, () => props.seekRequest], ([newSrc, newSeek], [oldSrc, oldSeek]) => {
      const srcChanged = newSrc !== oldSrc
      const seekChanged = newSeek !== oldSeek

      if (seekChanged && newSeek && newSeek.time !== null && newSeek.time !== undefined && newSeek.time >= 0) {
        // Store full object or minimal needed
        pendingSeek.value = { 
            time: newSeek.time, 
            playAfterSeek: !!newSeek.playAfterSeek 
        }
      }

      // If source changed, wait for loadedmetadata (do not seek immediately)
      // because readyState might still reflect the old video for a moment
      if (srcChanged) {
        return
      }

      // If source didn't change (or processed), try to seek if ready
      if (pendingSeek.value && videoRef.value && videoRef.value.readyState >= 1) {
         if (Math.abs(videoRef.value.currentTime - pendingSeek.value.time) > 0.05) {
           videoRef.value.currentTime = pendingSeek.value.time
         }
         
         if (pendingSeek.value.playAfterSeek) {
             videoRef.value.play().catch(e => console.error('Auto-play failed:', e))
         }

         pendingSeek.value = null // Handled
      }
    }, { flush: 'post' })

    return {
      videoRef,
      onTimeUpdate,
      onLoadedMetadata,
      onPlay,
      onPause,
      playVideo,
      pauseVideo,
      showHelp
    }
  }
}
</script>

<style scoped>
.video-player-container {
  width: 100%;
  background: #000;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px; /* Default height */
  height: 100%;
  position: relative;
}

.player-help-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  z-index: 20; /* Above video controls if needed, though native controls are shadow dom */
  transition: all 0.2s;
}

.player-help-btn:hover {
  background: rgba(0, 0, 0, 0.8);
  transform: scale(1.1);
}

.video-element {
  width: 100%;
  height: 100%;
  max-height: 100%;
  object-fit: contain;
}

.placeholder {
  color: #666;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.placeholder i {
  font-size: 48px;
}

/* Help Modal Styles */
.help-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
}

.help-modal {
  background: white;
  width: 600px;
  max-width: 90vw;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
}

.help-header {
  padding: 15px 20px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.help-header h3 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  line-height: 1;
}

.close-btn:hover {
  color: #666;
}

.help-content {
  padding: 20px;
  max-height: 70vh;
  overflow-y: auto;
}

.help-step {
  display: flex;
  margin-bottom: 20px;
  gap: 15px;
}

.step-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #e3f2fd;
  color: #1976d2;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 20px;
  flex-shrink: 0;
}

.step-info h4 {
  margin: 0 0 5px 0;
  color: #333;
  font-size: 16px;
}

.step-info p {
  margin: 0;
  color: #666;
  font-size: 14px;
  line-height: 1.5;
}

.help-footer {
  padding: 15px 20px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
}

.primary-btn {
  background-color: #1976d2;
  color: white;
  border: none;
  padding: 8px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.primary-btn:hover {
  background-color: #1565c0;
}
</style>
